import {
  createContext,
  type Dispatch,
  type FC,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import type { SnackbarProps } from '@crehana/ui';
import { useRouter } from 'next/router';

import { AMPLITUDE } from './constants';

interface AttributesUploadState {
  currentStep: number;
  handleBackStep: () => void;
  handleNextStep: () => void;
  resetDrawer: () => void;
  nextButtonIsActive: boolean;
  setNextButtonIsActive: Dispatch<boolean>;
  setSnackbar: Dispatch<SnackbarProps | 'defaultError'>;
  snackbar: SnackbarProps | 'defaultError';
}

export const AttributesUploadContext = createContext<
  AttributesUploadState | undefined
>(undefined);

const typeEvent = {
  2: AMPLITUDE.EVENT.CLICK.ETIQUETAS,
  3: AMPLITUDE.EVENT.CLICK.CONFIRMATION,
  4: AMPLITUDE.EVENT.CLICK.SUMMARY,
};

export const AttributesUploadProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const { slug } = router.query as { slug: string };

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [nextButtonIsActive, setNextButtonIsActive] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarProps | 'defaultError'>({});

  const handleNextStep = () => {
    logEvent({
      event: typeEvent[(currentStep + 1) as keyof typeof typeEvent],
      data: {
        Page: router.route.includes('subir-usuarios')
          ? AMPLITUDE.PAGE.USER_UPLOAD
          : AMPLITUDE.PAGE.ATTRIBUTES,
        Organization: slug,
        Success: true,
        ...getSourceEventParams(),
        Type: AMPLITUDE.TYPE.ACTION,
      },
    });
    setCurrentStep(currentStep + 1);
  };

  const handleBackStep = () => setCurrentStep(currentStep - 1);
  const resetDrawer = () => setCurrentStep(1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (snackbar === 'defaultError') {
      setSnackbar({
        open: true,
        state: 'alert',
        message: t('UPLOAD_DEFAULT_ERROR'),
      });
    }
  }, [snackbar]);

  const value = {
    currentStep,
    handleBackStep,
    handleNextStep,
    nextButtonIsActive,
    setNextButtonIsActive,
    setSnackbar,
    snackbar,
    resetDrawer,
  };

  return (
    <AttributesUploadContext.Provider value={value}>
      {children}
    </AttributesUploadContext.Provider>
  );
};

export function useAttributesUploadContext(): AttributesUploadState {
  const context = useContext(AttributesUploadContext);

  if (context === undefined) {
    throw new Error('AttributesUploadContext.Provider is not used.');
  }

  return context;
}
